<template>
  <div class="building-form">
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      class="building-form__form modern-dialog-form"
      @submit.prevent
    >
      <section class="dialog-section">
        <div class="dialog-section__body">
          <el-form-item label="中文名称" prop="chineseName">
            <el-input v-model="form.chineseName" placeholder="请输入中文名称" @blur="autoGenerateEnglish" />
          </el-form-item>

          <el-form-item label="英文名称" prop="englishName">
            <el-input v-model="form.englishName" placeholder="请输入英文名称">
              <template #suffix>
                <el-tooltip
                  placement="top"
                  content="根据中文名称自动生成英文名称"
                  effect="light"
                >
                  <button
                    type="button"
                    class="generate-english-btn"
                    @click="generateEnglishName"
                  >
                    <el-icon><Refresh /></el-icon>
                  </button>
                </el-tooltip>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="所属行政区" prop="regionId" class="form-item--full">
            <el-select
              v-model="form.regionId"
              placeholder="选择行政区域"
              filterable
              popper-class="region-dropdown"
            >
              <el-option
                v-for="region in regions"
                :key="region.id"
                :label="region.chinese_name"
                :value="region.id"
              >
                <div class="region-option">
                  <span>{{ region.chinese_name }}</span>
                  <span v-if="region.english_name" class="region-secondary">{{ region.english_name }}</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
        </div>
      </section>

      <section class="dialog-section">
        <div class="dialog-section__body dialog-section__body--inline">
          <el-form-item label="高度范围" class="form-item--height-range" required>
            <div class="height-inputs">
              <el-input-number
                v-model="form.shapeMinY"
                :controls="false"
                :step="1"
                :min="-1024"
                :max="1024"
                placeholder="最低 Y"
              />
              <span class="height-split">~</span>
              <el-input-number
                v-model="form.shapeMaxY"
                :controls="false"
                :step="1"
                :min="-1024"
                :max="1024"
                placeholder="最高 Y"
              />
            </div>
          </el-form-item>

          <el-form-item label="构建模式" class="form-item--mode">
            <el-radio-group v-model="form.mode">
              <el-radio-button label="rectangle">快速矩形</el-radio-button>
              <el-radio-button label="custom">自定义顶点</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            v-if="form.mode === 'rectangle'"
            label="矩形对角线"
            prop="rectangleCoords"
            class="form-item--full"
          >
            <div class="rectangle-inputs">
              <div class="rectangle-row">
                <el-input-number
                  v-model="rectangle.x1"
                  :controls="false"
                  :step="1"
                  placeholder="x1"
                  @change="triggerRectangleValidation"
                />
                <el-input-number
                  v-model="rectangle.z1"
                  :controls="false"
                  :step="1"
                  placeholder="z1"
                  @change="triggerRectangleValidation"
                />
                <span class="height-split">→</span>
                <el-input-number
                  v-model="rectangle.x2"
                  :controls="false"
                  :step="1"
                  placeholder="x2"
                  @change="triggerRectangleValidation"
                />
                <el-input-number
                  v-model="rectangle.z2"
                  :controls="false"
                  :step="1"
                  placeholder="z2"
                  @change="triggerRectangleValidation"
                />
                <el-button type="primary" size="small" @click="generateRectangle">生成矩形</el-button>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="顶点列表" prop="vertices" class="form-item--full">
            <div class="vertices-map-container">
              <div class="vertices-editor">
                <div class="vertex-row vertex-header">
                  <span>X</span>
                  <span>Z</span>
                  <span class="vertex-actions">操作</span>
                </div>
                <div
                  v-for="(vertex, index) in form.vertices"
                  :key="index"
                  class="vertex-row"
                >
                  <el-input-number
                    v-model="vertex.x"
                    :controls="false"
                    :step="1"
                    @change="triggerVerticesValidation"
                  />
                  <el-input-number
                    v-model="vertex.z"
                    :controls="false"
                    :step="1"
                    @change="triggerVerticesValidation"
                  />
                  <div class="vertex-actions">
                    <el-button
                      size="small"
                      :icon="Delete"
                      type="danger"
                      circle
                      @click="removeVertex(index)"
                      :disabled="form.vertices.length <= 3"
                    />
                  </div>
                </div>
                <div class="vertex-footer">
                  <el-button size="small" type="primary" :icon="Plus" @click="addVertex">新增顶点</el-button>
                </div>
              </div>
              <div v-if="mapPreviewUrl" class="map-preview">
                <iframe
                  :src="mapPreviewUrl"
                  frameborder="0"
                  allowfullscreen
                  class="map-iframe"
                />
              </div>
            </div>
          </el-form-item>
        </div>
      </section>

      <section class="dialog-section">
        <div class="dialog-section__body">
          <el-form-item label="Detail HTML" class="form-item--full">
            <el-input
              v-model="form.detail"
              placeholder="可选：用于 BlueMap detail 的 HTML 内容"
            />
            <div class="detail-preview" v-html="detailPreview"></div>
          </el-form-item>

          <el-form-item label="启用链接">
            <el-switch v-model="form.linkEnabled" />
          </el-form-item>

          <el-form-item
            v-if="form.linkEnabled"
            label="链接地址"
            prop="linkUrl"
            class="form-item--full"
          >
            <el-input v-model="form.linkUrl" placeholder="https://example.com" />
          </el-form-item>

          <el-form-item v-if="form.linkEnabled" label="新标签">
            <el-switch v-model="form.newTab" />
          </el-form-item>
        </div>
      </section>

      <div class="dialog-footer">
        <span class="dialog-footer__hint">保存后将自动刷新 BlueMap 关联数据</span>
        <div class="dialog-actions">
          <el-button @click="emit('cancel')">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">
            {{ isEdit ? '保存建筑' : '创建建筑' }}
          </el-button>
        </div>
      </div>
    </el-form>

    <section class="dialog-section form-summary">
      <div class="form-summary__body">
        <el-descriptions :column="isMobile ? 1 : 2" border>
          <el-descriptions-item label="中心坐标">
            <span v-if="centerPreview">({{ centerPreview.x }}, {{ centerPreview.y }}, {{ centerPreview.z }})</span>
            <span v-else>待计算</span>
          </el-descriptions-item>
          <el-descriptions-item label="顶点数量">
            {{ form.vertices.length }}
          </el-descriptions-item>
          <el-descriptions-item label="高度范围">
            {{ form.shapeMinY }} ~ {{ form.shapeMaxY }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import DOMPurify from 'dompurify'
import pinyin from 'pinyin'
import { Plus, Refresh, Delete } from '@element-plus/icons-vue'
import { buildingApi, exportApi } from '../api'
import configService from '../services/configService'

const props = defineProps({
  building: {
    type: Object,
    default: null
  },
  regions: {
    type: Array,
    default: () => []
  },
  defaultRegionId: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['success', 'cancel'])

const formRef = ref(null)
const submitting = ref(false)
const isMobile = ref(false)

const form = reactive({
  chineseName: '',
  englishName: '',
  regionId: null,
  shapeMinY: 0,
  shapeMaxY: 10,
  vertices: [],
  detail: '',
  linkEnabled: false,
  linkUrl: '',
  newTab: false,
  mode: 'rectangle'
})

const rectangle = reactive({
  x1: 0,
  z1: 0,
  x2: 16,
  z2: 16
})

const rules = {
  chineseName: [
    { required: true, message: '请输入中文名称', trigger: 'blur' }
  ],
  englishName: [
    { required: true, message: '请输入英文名称', trigger: 'blur' }
  ],
  regionId: [
    { required: true, message: '请选择行政区域', trigger: 'change' }
  ],
  rectangleCoords: [
    { validator: (_, value, callback) => validateRectangleCoords(callback), trigger: 'change' }
  ],
  vertices: [
    { validator: (_, value, callback) => validateVertices(callback), trigger: 'change' }
  ],
  linkUrl: [
    {
      validator: (_, value, callback) => {
        if (!form.linkEnabled) {
          callback()
          return
        }
        if (!value || !value.trim()) {
          callback(new Error('请输入链接地址'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ]
}

const isEdit = computed(() => Boolean(props.building && props.building.id))

const centerPreview = computed(() => {
  if (!form.vertices.length) return null
  const xs = form.vertices.map(v => Number(v.x)).filter(v => !Number.isNaN(v))
  const zs = form.vertices.map(v => Number(v.z)).filter(v => !Number.isNaN(v))
  if (!xs.length || !zs.length) return null
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minZ = Math.min(...zs)
  const maxZ = Math.max(...zs)
  const yMin = Number(form.shapeMinY)
  const yMax = Number(form.shapeMaxY)
  if (Number.isNaN(yMin) || Number.isNaN(yMax)) return null
  return {
    x: Math.round((minX + maxX) / 2),
    y: Math.round((yMin + yMax) / 2),
    z: Math.round((minZ + maxZ) / 2)
  }
})

const detailPreview = computed(() => DOMPurify.sanitize(form.detail || '<span class="detail-placeholder">暂无 detail 内容</span>'))

const mapPreviewUrl = computed(() => {
  if (!centerPreview.value) return null
  const { x, z } = centerPreview.value
  const mapX = x + 50
  const mapZ = z
  return `https://map.rofc.online/#overworld:${mapX}:30:${mapZ}:300:0.4:0.7:0:0:perspective`
})

const resetForm = () => {
  form.chineseName = ''
  form.englishName = ''
  form.regionId = props.defaultRegionId ?? null
  form.shapeMinY = 0
  form.shapeMaxY = 10
  form.vertices = []
  form.detail = ''
  form.linkEnabled = false
  form.linkUrl = ''
  form.newTab = false
  form.mode = 'rectangle'
  rectangle.x1 = 0
  rectangle.z1 = 0
  rectangle.x2 = 16
  rectangle.z2 = 16
}

const populateForm = () => {
  if (!props.building) {
    resetForm()
    return
  }
  form.chineseName = props.building.chinese_name || ''
  form.englishName = props.building.english_name || ''
  form.regionId = props.building.region_id || props.defaultRegionId || null
  form.shapeMinY = props.building.shape_min_y ?? 0
  form.shapeMaxY = props.building.shape_max_y ?? 0
  form.vertices = (props.building.shape_coordinates || []).map(point => ({
    x: Number(point.x),
    z: Number(point.z)
  }))
  form.detail = props.building.detail || ''
  form.linkEnabled = Boolean(props.building.link_enabled)
  form.linkUrl = props.building.link_url || ''
  form.newTab = Boolean(props.building.new_tab)
  form.mode = 'custom'
  if (form.vertices.length >= 2) {
    const xs = form.vertices.map(v => Number(v.x))
    const zs = form.vertices.map(v => Number(v.z))
    rectangle.x1 = Math.min(...xs)
    rectangle.x2 = Math.max(...xs)
    rectangle.z1 = Math.min(...zs)
    rectangle.z2 = Math.max(...zs)
  }
}

const generateEnglishName = () => {
  if (!form.chineseName) {
    ElMessage.warning('请先输入中文名称')
    return
  }
  const result = pinyin(form.chineseName, { style: pinyin.STYLE_NORMAL })
    .flat()
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
  form.englishName = result.join(' ')
}

const autoGenerateEnglish = () => {
  if (!isEdit.value && (!form.englishName || !form.englishName.trim())) {
    generateEnglishName()
  }
}

const ensureVerticesValid = () => {
  form.vertices = form.vertices
    .map(vertex => ({
      x: Number(vertex.x),
      z: Number(vertex.z)
    }))
    .filter(vertex => !Number.isNaN(vertex.x) && !Number.isNaN(vertex.z))
}

const validateRectangleCoords = (callback) => {
  if (form.mode !== 'rectangle') {
    callback()
    return
  }
  const { x1, z1, x2, z2 } = rectangle
  if (x1 === null || x1 === undefined || z1 === null || z1 === undefined || 
      x2 === null || x2 === undefined || z2 === null || z2 === undefined) {
    callback(new Error('请完整输入矩形对角线坐标'))
    return
  }
  callback()
}

const validateVertices = (callback) => {
  ensureVerticesValid()
  if (!form.vertices.length) {
    callback(new Error('请添加建筑顶点'))
    return
  }
  if (form.vertices.length < 3) {
    callback(new Error('建筑至少需要三个顶点'))
    return
  }
  callback()
}

const triggerVerticesValidation = () => {
  formRef.value?.validateField('vertices')
}

const triggerRectangleValidation = () => {
  formRef.value?.validateField('rectangleCoords')
}

const generateRectangle = () => {
  const { x1, z1, x2, z2 } = rectangle
  if ([x1, z1, x2, z2].some(value => value === null || value === undefined)) {
    ElMessage.error('请完整输入矩形对角线坐标')
    return
  }
  const minX = Math.min(x1, x2)
  const maxX = Math.max(x1, x2)
  const minZ = Math.min(z1, z2)
  const maxZ = Math.max(z1, z2)
  form.vertices = [
    { x: minX, z: minZ },
    { x: minX, z: maxZ },
    { x: maxX, z: maxZ },
    { x: maxX, z: minZ }
  ]
  triggerVerticesValidation()
}

const addVertex = () => {
  form.vertices.push({ x: 0, z: 0 })
  triggerVerticesValidation()
}

const removeVertex = (index) => {
  if (form.vertices.length <= 3) {
    ElMessage.warning('至少需要三个顶点')
    return
  }
  form.vertices.splice(index, 1)
  triggerVerticesValidation()
}

// 自动同步到 BlueMap 服务器
const syncToBlueMap = async () => {
  try {
    console.log('🔄 自动同步建筑配置到 BlueMap 服务器...')
    
    // 获取当前保存的全局配置
    const apiConfig = configService.getApiConfig()
    
    // 使用全局配置进行推送
    await exportApi.pushToBlueMap({ 
      include_config_header: true,
      ...apiConfig
    })
    
    console.log('✅ BlueMap 配置已自动更新（包含建筑数据）')
  } catch (error) {
    // 静默处理错误，不影响主流程
    console.warn('⚠️ BlueMap 自动同步失败（不影响建筑保存）:', error.message)
  }
}

const handleSubmit = () => {
  if (!formRef.value) return
  formRef.value.validate(async (valid) => {
    if (!valid) return
    if (Number(form.shapeMinY) >= Number(form.shapeMaxY)) {
      ElMessage.error('最高高度必须大于最低高度')
      return
    }
    ensureVerticesValid()
    if (form.vertices.length < 3) {
      ElMessage.error('建筑至少需要三个顶点')
      return
    }
    submitting.value = true
    try {
      const payload = {
        chinese_name: form.chineseName.trim(),
        english_name: form.englishName.trim(),
        region_id: form.regionId,
        shape_min_y: Number(form.shapeMinY),
        shape_max_y: Number(form.shapeMaxY),
        shape_coordinates: form.vertices.map(vertex => ({
          x: Number(vertex.x),
          z: Number(vertex.z)
        })),
        detail: form.detail || '',
        link_enabled: Boolean(form.linkEnabled),
        link_url: form.linkEnabled ? form.linkUrl.trim() : null,
        new_tab: form.linkEnabled ? Boolean(form.newTab) : false
      }
      if (isEdit.value) {
        await buildingApi.update(props.building.id, payload)
      } else {
        await buildingApi.create(payload)
      }
      
      // 自动同步到 BlueMap 服务器（静默执行）
      syncToBlueMap()
      
      emit('success')
    } catch (error) {
      ElMessage.error(error.message || '保存建筑失败')
    } finally {
      submitting.value = false
    }
  })
}

const handleResize = () => {
  isMobile.value = window.innerWidth < 768
}

watch(() => props.building, populateForm, { immediate: true })
watch(() => props.defaultRegionId, (value) => {
  if (!isEdit.value && !form.regionId) {
    form.regionId = value
  }
})

onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
  if (!props.building) {
    resetForm()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(() => form.vertices.length, () => {
  triggerVerticesValidation()
})
</script>

<style scoped>
.building-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.building-form__form {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.building-form :deep(.dialog-section) {
  padding: 12px 16px;
}

/* 缩小各节标题和图标 */
.building-form :deep(.dialog-section__header) {
  padding: 4px 0 2px;
  gap: 6px;
  margin-bottom: 4px;
}

.building-form :deep(.dialog-section__icon) {
  font-size: 16px;
  width: 28px;
  height: 28px;
  line-height: 28px;
}

.building-form :deep(.dialog-section__title) {
  font-size: 14px;
  margin-bottom: 1px;
  line-height: 1.2;
}

.building-form :deep(.dialog-section__subtitle) {
  font-size: 11px;
  line-height: 1.3;
}

.building-form :deep(.dialog-section__body) {
  padding: 4px 0;
  gap: 4px;
}

/* 调整表单项间距 */
.building-form :deep(.el-form-item) {
  margin-bottom: 4px;
}

.building-form :deep(.el-form-item__label) {
  line-height: 1.2;
  margin-bottom: 2px;
  font-size: 12px;
  padding-bottom: 2px;
}

/* 高度范围和构建模式同行显示 */
.building-form :deep(.dialog-section__body--inline) {
  grid-template-columns: 1fr 1fr;
  display: grid;
  gap: 16px;
}

.building-form :deep(.form-item--height-range),
.building-form :deep(.form-item--mode) {
  margin-bottom: 0;
}

.height-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(244, 127, 76, 0.08);
  padding: 6px 10px;
  border-radius: 10px;
}

.height-split {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 13px;
}

.rectangle-inputs {
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(244, 127, 76, 0.08);
  align-items: center;
}

.rectangle-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.rectangle-row .el-button {
  margin-left: auto;
}

.form-item--full {
  width: 100%;
}

.form-item--full :deep(.el-form-item__content) {
  width: 100%;
}

.vertices-map-container {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
}

.vertices-editor {
  border: 1px solid rgba(244, 127, 76, 0.16);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  flex-shrink: 0;
  width: 240px;
}

.map-preview {
  flex: 1;
  min-height: 320px;
  border: 1px solid rgba(244, 127, 76, 0.16);
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
}

.map-iframe {
  width: 100%;
  height: 100%;
  min-height: 320px;
  display: block;
}

.vertex-row {
  display: grid;
  grid-template-columns: 80px 80px 60px;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(244, 127, 76, 0.12);
}

.vertex-header {
  background: rgba(244, 127, 76, 0.12);
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 11px;
  padding: 5px 10px;
}

.vertex-row:last-child {
  border-bottom: none;
}

.vertex-actions {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.vertex-footer {
  padding: 6px 10px;
  background: rgba(244, 127, 76, 0.08);
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .vertices-map-container {
    flex-direction: column;
  }

  .vertices-editor {
    width: 100%;
  }

  .map-preview {
    min-height: 240px;
  }

  .map-iframe {
    min-height: 240px;
  }
}

.region-option {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  align-items: flex-start;
}

.region-secondary {
  font-size: 11px;
  color: #909399;
  line-height: 1.2;
}

.detail-preview {
  margin-top: 4px;
  border: 1px dashed rgba(244, 127, 76, 0.3);
  border-radius: 8px;
  padding: 8px;
  min-height: 40px;
  font-size: 12px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.88);
  line-height: 1.4;
}

.detail-placeholder {
  color: var(--text-secondary);
  opacity: 0.8;
}

.form-summary {
  margin-top: 4px;
}

.form-summary__body .el-descriptions {
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
}

/* 优化输入框尺寸 */
.building-form :deep(.el-input-number) {
  width: 100%;
}

.building-form :deep(.el-input-number .el-input__wrapper) {
  padding: 2px 8px;
  box-shadow: 0 0 0 1px rgba(244, 127, 76, 0.18) inset;
  transition: box-shadow 0.15s ease;
}

.building-form :deep(.el-input-number.is-focus .el-input__wrapper),
.building-form :deep(.el-input-number .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1.5px rgba(244, 127, 76, 0.36) inset;
}

.generate-english-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-left: 4px;
  border: 1px solid rgba(244, 127, 76, 0.22);
  border-radius: 6px;
  background: rgba(244, 127, 76, 0.12);
  color: #f47f4c;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  font-size: 14px;
}

.generate-english-btn:hover {
  background: rgba(244, 127, 76, 0.2);
  border-color: rgba(244, 127, 76, 0.32);
  color: #e86a32;
}

.generate-english-btn:active {
  background: rgba(244, 127, 76, 0.32);
  transform: scale(0.96);
}

.generate-english-btn:focus-visible {
  outline: 2px solid rgba(244, 127, 76, 0.45);
  outline-offset: 2px;
}

.building-form :deep(.el-input__wrapper) {
  padding: 2px 8px;
  border: none;
  box-shadow: 0 0 0 1px rgba(244, 127, 76, 0.18) inset !important;
  transition: box-shadow 0.15s ease;
}

.building-form :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px rgba(244, 127, 76, 0.28) inset !important;
}

.building-form :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1.5px rgba(244, 127, 76, 0.36) inset !important, 0 4px 10px rgba(244, 127, 76, 0.18) !important;
}

.building-form :deep(.el-input),
.building-form :deep(.el-select),
.building-form :deep(.el-input-number) {
  --el-input-border-color: rgba(244, 127, 76, 0.18);
  --el-input-hover-border-color: rgba(244, 127, 76, 0.28);
  --el-input-focus-border-color: rgba(244, 127, 76, 0.48);
  --el-input-focus-border: rgba(244, 127, 76, 0.48);
  --el-select-input-focus-border-color: rgba(244, 127, 76, 0.48);
  --el-select-input-border-color-hover: rgba(244, 127, 76, 0.28);
  --el-color-primary: #f47f4c;
}

.building-form :deep(.el-input__inner) {
  height: 24px;
  line-height: 24px;
  font-size: 13px;
}

.building-form :deep(.el-textarea__inner) {
  padding: 6px 8px;
  line-height: 1.4;
}

.building-form :deep(.el-select .el-input__wrapper) {
  padding: 2px 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.88);
  border: none;
  box-shadow: 0 0 0 1px rgba(244, 127, 76, 0.22) inset !important;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.building-form :deep(.el-select .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px rgba(244, 127, 76, 0.32) inset !important;
}

.building-form :deep(.el-select.is-focus .el-input__wrapper),
.building-form :deep(.el-select .el-input.is-focus .el-input__wrapper),
.building-form :deep(.el-select .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1.5px rgba(244, 127, 76, 0.48) inset !important, 0 6px 14px rgba(244, 127, 76, 0.18) !important;
  background: rgba(255, 255, 255, 0.96);
}
.building-form :deep(.el-select .el-select__caret) {
  color: #f47f4c;
  transition: color 0.15s ease;
}

.building-form :deep(.el-select.is-focus .el-select__caret) {
  color: #e86a32;
}

.building-form :deep(.el-button) {
  padding: 6px 12px;
  height: 28px;
  font-size: 13px;
}

.building-form :deep(.el-button--small) {
  padding: 4px 10px;
  height: 24px;
  font-size: 12px;
}

.building-form :deep(.el-button.is-circle) {
  padding: 4px;
  width: 24px;
  height: 24px;
}

.building-form :deep(.el-radio-button__inner) {
  padding: 6px 12px;
  font-size: 13px;
  height: 28px;
  line-height: 16px;
}

.building-form :deep(.el-radio-button.is-active .el-radio-button__inner) {
  background-color: rgba(244, 127, 76, 0.12) !important;
  border-color: rgba(244, 127, 76, 0.32) !important;
  color: #f47f4c !important;
  box-shadow: -1px 0 0 0 rgba(244, 127, 76, 0.32) !important;
}

.building-form :deep(.el-switch) {
  height: 20px;
}

.building-form :deep(.el-switch.is-checked .el-switch__core) {
  background-color: #f47f4c !important;
  border-color: #f47f4c !important;
}

.building-form :deep(.dialog-footer) {
  padding-top: 8px;
  margin-top: 8px;
}

.building-form :deep(.dialog-footer__hint) {
  font-size: 11px;
  line-height: 1.3;
}
</style>

<style>
/* 非 scoped 样式，用于 popper 下拉框（渲染在 body 下） */
.region-dropdown {
  border-radius: 14px !important;
  padding: 0 !important;
  background: #fffdfa;
  border: 1px solid rgba(244, 127, 76, 0.28);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
  max-height: 400px !important;
  overflow: hidden !important;
}

.region-dropdown .el-select-dropdown__list {
  padding: 6px 0 10px 0 !important;
  margin: 0 !important;
}

.region-dropdown .el-scrollbar {
  border-radius: 14px;
}

.region-dropdown .el-scrollbar__view {
  border-radius: 14px;
}

.region-dropdown .el-select-dropdown__item {
  font-size: 13px;
  padding: 8px 14px;
  margin: 0 6px;
  border-radius: 8px;
  min-height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  color: #51586f;
  transition: background 0.12s ease, color 0.12s ease;
  line-height: 1.4;
}

.region-dropdown .el-select-dropdown__item:first-child {
  margin-top: 0;
}

.region-dropdown .el-select-dropdown__item.is-hovering {
  background: rgba(244, 127, 76, 0.12);
  color: #e46933;
}

.region-dropdown .el-select-dropdown__item.is-selected {
  background: rgba(244, 127, 76, 0.18);
  color: #d65924;
  font-weight: 600;
}

.region-dropdown .el-scrollbar__wrap {
  max-height: 400px !important;
  padding-bottom: 4px !important;
}

.region-dropdown .el-popper__arrow::before {
  display: none !important;
}

.region-dropdown .el-select-dropdown__empty,
.region-dropdown .el-select-dropdown__loading {
  padding: 6px 14px !important;
}

/* 移除可能的分隔线 */
.region-dropdown::before,
.region-dropdown::after {
  display: none !important;
}

.region-dropdown .el-select-dropdown__list::before {
  display: none !important;
}
</style>
